import { Box, UnstyledButton } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import PdfPreview from "components/PdfPreview";
import { useEffect, useState } from "react";
import { DocumentWithId } from "types/document";

interface NavButtonProps {
  onClick: () => void;
  enabled: boolean;
  label: string;
  children: React.ReactNode;
}

const NavButton: React.FC<NavButtonProps> = ({
  onClick,
  enabled,
  label,
  children,
}) => (
  <UnstyledButton
    onClick={onClick}
    disabled={!enabled}
    aria-label={label}
    style={{
      flexShrink: 0,
      width: 36,
      height: 36,
      borderRadius: "50%",
      backgroundColor: "rgba(60,60,60,0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: enabled ? 1 : 0,
    }}
  >
    {children}
  </UnstyledButton>
);

interface ChatImagePreviewImageAreaProps {
  documents: DocumentWithId[];
  selectedImageIndex: number;
  setSelectedImageIndex: React.Dispatch<React.SetStateAction<number>>;
  isDesktop: boolean;
}

export const ChatImagePreviewImageArea: React.FC<
  ChatImagePreviewImageAreaProps
> = ({ documents, selectedImageIndex, setSelectedImageIndex, isDesktop }) => {
  const goToPrev = () => setSelectedImageIndex(i => Math.max(0, i - 1));
  const goToNext = () =>
    setSelectedImageIndex(i => Math.min(documents.length - 1, i + 1));

  const canGoPrev = documents.length > 1 && selectedImageIndex > 0;
  const canGoNext =
    documents.length > 1 && selectedImageIndex < documents.length - 1;

  const currentBlob = documents[selectedImageIndex]?.blob;
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!currentBlob) {
      setPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(currentBlob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [currentBlob]);

  const isPdf = currentBlob?.type === "application/pdf";

  return (
    <Box
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        ...(isDesktop ? {} : { maxHeight: "50vh" }),
      }}
    >
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          width: "100%",
          maxWidth: isDesktop ? 800 : undefined,
          maxHeight: "50vh",
          padding: isDesktop ? "0 16px" : 0,
        }}
      >
        {isDesktop && (
          <NavButton
            onClick={goToPrev}
            enabled={canGoPrev}
            label="Previous image"
          >
            <IconChevronLeft size={20} color="white" />
          </NavButton>
        )}

        <Box
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxHeight: "50vh",
            overflow: "hidden",
          }}
        >
          {currentBlob && (
            isPdf ? (
              <PdfPreview preview={previewUrl} />
            ) : (
              <img
                src={previewUrl}
                style={{
                  maxWidth: "100%",
                  maxHeight: "50vh",
                  objectFit: "contain",
                  width: "auto",
                  height: "auto",
                }}
                alt="Selected document"
              />
            )
          )}
        </Box>

        {isDesktop && (
          <NavButton onClick={goToNext} enabled={canGoNext} label="Next image">
            <IconChevronRight size={20} color="white" />
          </NavButton>
        )}
      </Box>
    </Box>
  );
};
